/* eslint-disable no-irregular-whitespace */
/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { toNDigits } from '@mail/cross-sizes-utils';
import { Icon20ExclamationMarkOutline } from '@vkontakte/icons';
import classNames from 'clsx';
import React, { type ReactElement, memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IS_PHONE_BROWSER } from 'reactApp/appHelpers/configHelpers';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { isRebranding } from 'reactApp/appHelpers/featuresHelpers';
import {
    allowedStatusesForBazModalAfterOverquota,
    isShowBazModalAfterCloseOverquota,
} from 'reactApp/appHelpers/featuresHelpers/features/bazModalAfterCloseOverquota';
import { overquotaAbNoClear } from 'reactApp/appHelpers/featuresHelpers/features/overquotaAbNoClear';
import {
    overquotaAbTariffB,
    overquotaAbTariffBC,
    overquotaAbTariffC,
    overquotaAbTariffD,
    overquotaAbTariffName,
    overquotaAbVariantTariffId,
} from 'reactApp/appHelpers/featuresHelpers/features/overquotaAbTariff';
import { overquotaBlockDate } from 'reactApp/appHelpers/featuresHelpers/features/overquotaBlockDate';
import { overquotaNewCommunication } from 'reactApp/appHelpers/featuresHelpers/features/overquotaNewCommunication';
import { overquotaNewModalMoreInfoUrl } from 'reactApp/appHelpers/featuresHelpers/features/overquotaNewFullBlocked';
import { PaymentUTM } from 'reactApp/constants/paymentUTM';
import { sendDwhModal } from 'reactApp/modules/dwh/dwh.helpers';
import { getPaySource } from 'reactApp/modules/payment/payment.analytics';
import { openTariffBuy } from 'reactApp/modules/payment/payment.module';
import { updateProducts } from 'reactApp/modules/products/products.module';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { getUserOverquotaState } from 'reactApp/modules/profile/profile.selectors';
import { historyPush } from 'reactApp/modules/router/router.module';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import type { RootState } from 'reactApp/store';
import { renderBazModalAfterCloseOverquotaModal } from 'reactApp/ui/BazModal/AfterCloseOverquotaModal';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import Content from 'reactApp/ui/Content/Content';
import { Dialog } from 'reactApp/ui/Dialog/Dialog';
import { formatFileHistoryDate } from 'reactApp/utils/formatDate';
import { sendPaymentGa } from 'reactApp/utils/paymentGa';

import styles from './OverquotaModal.css';
import { OverquotaBannerCtrl, sendDwhOverquota, sendTechOverqRadar } from './OverquotaModal.helpers';
import { type OverquotaModalProps, OverquotaModalMode } from './OverquotaModal.types';

const ACTION_NOT_ENOUGH_SPACE = 'not-enough-space';
const ACTION_STOP_FILES_OPEN = 'stop-files-open';

const place = 'overquota_started';

const name = overquotaAbNoClear ? 'overquota_modal_cloud' : 'overquota_splash_new';

export const OverquotaModal = memo(function OverquotaPopup({ onClose, mode }: OverquotaModalProps): ReactElement | null {
    const storage = useSelector(getCurrentStorage);
    const { used, userTotalSpace } = useSelector(UserQuotaSelectors.getUserQuotaState);
    const overquota = useSelector(UserQuotaSelectors.getOverQuota);
    const { trashSize = 0, blockTime = undefined, state, clearTime } = useSelector(getUserOverquotaState);
    const theFirstStep = overquotaBlockDate && blockTime && state === 'started';
    const theSecondStep = overquotaBlockDate && blockTime && (state === 'blocked' || state === 'unblocked');
    const productId = overquotaAbVariantTariffId;
    const product = useSelector((state: RootState) => ProductsSelectors.getProductById(state, productId));

    const clearTrash = trashSize > 0;

    const showClearButton = overquotaAbNoClear !== 'b' && !overquotaAbTariffBC && state !== 'full_blocked';

    const showModal = Boolean(used && userTotalSpace);

    const dispatch = useDispatch();

    const getModalName = useCallback(() => {
        if (state === 'full_blocked') {
            return 'mnz_overquota_after_94_day';
        }

        return overquotaAbTariffName ? `mnz_overquota_${overquotaAbTariffName}` : 'overquota_splash_new';
    }, [state]);

    useEffect(() => {
        if (!productId || product || !showModal) {
            return;
        }

        dispatch(updateProducts({ productId }));
    }, [productId, product, showModal]);

    useEffect(() => {
        if (!showModal) {
            return;
        }

        sendTechOverqRadar({ state });

        sendDwhOverquota({ action: 'show', storage, place, name });

        if (mode === OverquotaModalMode.enter) {
            emitAnalyticEvent(AnalyticEventNames.BLOCK_OVERQUOTA_VIEW);
            sendPaymentGa({
                action: ACTION_NOT_ENOUGH_SPACE,
                label: 'view',
            });
        }

        if (mode === OverquotaModalMode.viewer) {
            emitAnalyticEvent(AnalyticEventNames.BLOCK_OVERQUOTA_FILES_VIEW);
            sendPaymentGa({
                action: ACTION_STOP_FILES_OPEN,
                label: 'view',
                place: storage,
            });
        }

        sendDwhModal({ name: getModalName() });
    }, [showModal]);

    const title = useMemo(() => {
        if (overquotaNewCommunication) {
            if (mode === OverquotaModalMode.viewer) {
                return 'Смотреть файлы пока нельзя';
            }

            if (overquotaBlockDate && blockTime && state === 'started') {
                return (
                    <>
                        <span>{formatFileHistoryDate(blockTime)}</span> доступ к файлам будет ограничен, но есть решение
                    </>
                );
            }

            if (overquotaBlockDate && clearTime && (state === 'unblocked' || state === 'full_blocked')) {
                return (
                    <>
                        <span>{formatFileHistoryDate(clearTime)}</span> ваши файлы будут удалены. Как не допустить
                    </>
                );
            }
        }

        if (mode === OverquotaModalMode.viewer) {
            return 'Посмотреть файлы пока нельзя';
        }

        if (state === 'full_blocked') {
            return 'Файлы будут удалены — как не допустить';
        }

        if (overquotaBlockDate && blockTime && state === 'started') {
            return (
                <>
                    <span>{formatFileHistoryDate(blockTime)}</span> доступ к файлам будет заблокирован, но есть решение
                </>
            );
        }

        if (overquotaBlockDate && clearTime && state === 'unblocked') {
            return (
                <>
                    <span>{formatFileHistoryDate(clearTime)}</span> ваши файлы будут удалены
                </>
            );
        }

        return 'Вашим файлам не хватает места';
    }, [mode, blockTime, overquotaBlockDate, state]);

    const content = useMemo(() => {
        if (overquotaNewCommunication) {
            if (mode === OverquotaModalMode.viewer) {
                return (
                    <>
                        Единое пространство Облака и Почты переполнено на <span>{overquota.value}</span> — увеличьте его с подпиской
                        Mail Space, чтобы снова просматривать файлы
                    </>
                );
            }

            if (clearTrash) {
                return (
                    <>
                        Единое пространство Облака и Почты переполнено. Увеличьте его с подпиской Mail Space или очистите{' '}
                        <span>
                            {
                                toNDigits({
                                    bytes: trashSize,
                                    maxDigitsCount: 3,
                                }).value
                            }
                        </span>{' '}
                        в корзине
                    </>
                );
            }

            if (overquotaBlockDate && blockTime && state === 'started') {
                return (
                    <>
                        Единое пространство Облака и Почты переполнено на <span>{overquota.value}</span> — нельзя будет загружать, смотреть
                        и отправлять файлы. Увеличьте место с подпиской Mail Space.{' '}
                        <a href={overquotaNewModalMoreInfoUrl} target="_blank" rel="noreferrer">
                            Подробнее
                        </a>
                    </>
                );
            }

            if (overquotaBlockDate && clearTime && (state === 'unblocked' || state === 'full_blocked')) {
                return (
                    <>
                        Единое пространство Облака и Почты переполнено на <span>{overquota.value}</span> — увеличьте его с подпиской
                        Mail Space. Если ничего не сделать, оно автоматически очистится.{' '}
                        <a href={overquotaNewModalMoreInfoUrl} target="_blank" rel="noreferrer">
                            Подробнее
                        </a>
                    </>
                );
            }
        }

        if (overquotaAbTariffBC && state !== 'full_blocked') {
            return overquotaAbTariffC ? (
                <>
                    Пространство для хранения ваших файлов переполнено.
                    <br />
                    Увеличьте его с подпиской Mail Space.
                    <br />
                    {product?.space?.value ? `${product?.space?.value} ваши со скидкой 50%` : ''}
                </>
            ) : (
                <>
                    Пространство для хранения ваших файлов переполнено.
                    <br />
                    Увеличьте его с подпиской Mail Space. {product?.space?.value ? `${product?.space?.value} ваши за 1 ₽` : ''}
                </>
            );
        }

        if (mode === OverquotaModalMode.viewer) {
            return (
                <>
                    Облако переполнено на <span>{overquota.value}</span> — увеличьте место или удалите лишнее, чтобы снова просматривать
                    файлы
                </>
            );
        }

        if (mode === OverquotaModalMode.enter) {
            return (
                <>
                    Они занимают на <span>{overquota.value}</span> больше, чем у вас есть — некоторые функции недоступны. Увеличьте место
                    или удалите лишнее
                </>
            );
        }

        if (clearTrash) {
            return (
                <>
                    Увеличьте место или очистите корзину. Файлы в ней занимают 
                    <span className={styles.quotaSize}>
                        {' '}
                        {
                            toNDigits({
                                bytes: trashSize,
                                maxDigitsCount: 3,
                            }).value
                        }
                    </span>
                     — этого хватит, чтобы сохранить доступ
                </>
            );
        }

        if (state === 'full_blocked') {
            return (
                <>
                    Память в Облаке и Почте переполнена. Удалите лишнее или увеличьте её с подпиской Mail Space. Если ничего не сделать,
                    место автоматически очистится.{' '}
                    <a href={overquotaNewModalMoreInfoUrl} target="_blank" rel="noreferrer">
                        Подробнее
                    </a>
                </>
            );
        }

        if (overquotaBlockDate && (blockTime || clearTime)) {
            return (
                <>
                    Пространство для хранения ваших файлов переполнено. Увеличьте его или очистите от лишнего.{' '}
                    <a href={overquotaNewModalMoreInfoUrl} target="_blank" rel="noreferrer">
                        Подробнее
                    </a>
                </>
            );
        }

        return (
            <>
                Скоро они{' '}
                <a href="https://help.mail.ru/legal/terms/cloud/LA" target="_blank" rel="noreferrer">
                    будут удалены
                </a>
                . Увеличьте место, чтобы их сохранить, или очистите Облако от ненужных файлов
            </>
        );
    }, [mode, overquota, clearTrash, blockTime, overquotaBlockDate, trashSize, product?.space?.value]);

    const clearButtonText = useMemo(() => {
        if (clearTrash) {
            return 'Перейти в корзину';
        }

        if (overquotaBlockDate && blockTime) {
            return 'Очистить';
        }

        return 'Очистить Облако';
    }, [clearTrash, blockTime, overquotaBlockDate]);

    const buttonTitle = useMemo(() => {
        if (overquotaNewCommunication) {
            if (mode === OverquotaModalMode.viewer) {
                return 'Снять ограничение';
            }

            if (overquotaBlockDate && blockTime && state === 'started') {
                return 'Снять ограничение';
            }

            if (overquotaBlockDate && clearTime && (state === 'unblocked' || state === 'full_blocked')) {
                return 'Увеличить место';
            }
        }

        if (overquotaAbTariffB && state !== 'full_blocked') {
            return 'Снять ограничение за 1 ₽';
        }
        if (overquotaAbTariffC && state !== 'full_blocked') {
            return 'Снять ограничение со скидкой';
        }

        return 'Увеличить место';
    }, [state]);

    const onPrimaryHandler = useCallback(() => {
        sendDwhOverquota({ action: 'click', storage, control: OverquotaBannerCtrl.add_space, place, name });

        let search = PaymentUTM.overquotaSplashNew;

        if (mode === OverquotaModalMode.enter) {
            search = PaymentUTM.overquotaSlashEnterSpace;
            emitAnalyticEvent(AnalyticEventNames.BLOCK_OVERQUOTA_CLICK);
            sendPaymentGa({
                action: ACTION_NOT_ENOUGH_SPACE,
                label: 'click',
            });
        }

        if (mode === OverquotaModalMode.viewer) {
            search = PaymentUTM.overquotaSlashViewerSpace;
            emitAnalyticEvent(AnalyticEventNames.BLOCK_OVERQUOTA_FILES_CLICK);
            sendPaymentGa({
                action: ACTION_STOP_FILES_OPEN,
                label: 'click',
                place: storage,
            });
        }

        if (theFirstStep) {
            search = PaymentUTM.overquotaPopupBlockFirstStepIncreaseSpace;
        }

        if (theSecondStep) {
            search = PaymentUTM.overquotaPopupBlockSecondStepIncreaseSpace;
        }

        if (state === 'full_blocked') {
            search = PaymentUTM.overquotaFullBlockedNewModal;
        }

        sendDwhModal({ action: 'click', name: getModalName(), button_name: OverquotaBannerCtrl.add_space });

        if ((overquotaAbTariffBC || overquotaAbTariffD) && state !== 'full_blocked') {
            dispatch(
                openTariffBuy({
                    productId,
                    isMobile: IS_PHONE_BROWSER,
                    title: overquotaAbTariffB
                        ? 'Подписка Mail Space на месяц за 1 ₽'
                        : overquotaAbTariffC
                        ? 'Подписка Mail Space на год со скидкой'
                        : '',
                    paySource: getPaySource(overquotaAbTariffName ? `overquota_modal_${overquotaAbTariffName}` : 'modal_overquota_cleaner'),
                })
            );
        } else {
            dispatch(historyPush({ id: '/promo/quota', search: `?${search}` }));
        }

        onClose?.();

        sendTechOverqRadar({ state, action: 'click', bttnName: 'increase' });
    }, [onClose, mode, storage, dispatch, productId, state]);

    const onSecondaryHandler = useCallback(() => {
        sendDwhOverquota({ action: 'click', storage, control: OverquotaBannerCtrl.clear, place, name });

        let search = '';

        if (mode === OverquotaModalMode.enter) {
            search = PaymentUTM.overquotaSlashEnterCleaner;
            sendPaymentGa({
                action: ACTION_NOT_ENOUGH_SPACE,
                label: 'cleaner',
            });
        }

        if (mode === OverquotaModalMode.viewer) {
            search = PaymentUTM.overquotaSlashViewerCleaner;
            sendPaymentGa({
                action: ACTION_STOP_FILES_OPEN,
                label: 'cleaner',
                place: storage,
            });
        }

        if (theFirstStep) {
            search = PaymentUTM.overquotaPopupBlockFirstStepClearSpace;
        }
        if (theSecondStep) {
            search = PaymentUTM.overquotaPopupBlockSecondStepClearSpace;
        }

        sendDwhModal({ action: 'click', name: getModalName(), button_name: OverquotaBannerCtrl.clear });

        if (clearTrash) {
            dispatch(
                historyPush({
                    id: EStorageType.trashbin,
                    search: `?${search}`,
                })
            );
        } else {
            dispatch(
                historyPush({
                    id: '/promo/quota',
                    search: `?${search}`,
                    hash: 'cleaner',
                })
            );
        }
        onClose?.();

        sendTechOverqRadar({ state, action: 'click', bttnName: 'clear' });
    }, [onClose, mode, storage, dispatch, clearTrash]);

    const onCloseHandler = useCallback(() => {
        sendDwhOverquota({ action: 'click', storage, control: OverquotaBannerCtrl.closed, place, name });

        if (mode === OverquotaModalMode.enter) {
            sendPaymentGa({
                action: ACTION_NOT_ENOUGH_SPACE,
                label: 'close',
            });
        }

        if (mode === OverquotaModalMode.viewer) {
            sendPaymentGa({
                action: ACTION_STOP_FILES_OPEN,
                label: 'close',
                place: storage,
            });
        }

        sendDwhModal({ action: 'close', name: getModalName() });

        onClose?.();

        if (
            isShowBazModalAfterCloseOverquota &&
            state &&
            allowedStatusesForBazModalAfterOverquota.includes(state) &&
            mode !== OverquotaModalMode.viewer
        ) {
            renderBazModalAfterCloseOverquotaModal();
        }
    }, [onClose, mode, storage]);

    const icon = useMemo(() => {
        if (overquotaAbTariffBC && state !== 'full_blocked') {
            return <div className={styles.quotaIconB}>{overquotaAbTariffB ? 'Решение за 1 ₽' : 'Есть решение'}</div>;
        }

        return (
            <div className={styles.quotaIcon}>
                <Icon20ExclamationMarkOutline width={26} height={26} />
            </div>
        );
    }, [state]);

    if (!showModal) {
        return null;
    }

    return (
        <Dialog
            open
            header={''}
            closeIconWrapperClassName={styles.close}
            closeOnDimmerClick={false}
            id="overquota-alert"
            qaId="overquota-alert"
            onCancel={onCloseHandler}
            className={classNames(styles.root, {
                [styles.rebranding]: isRebranding,
            })}
            size="tiny"
        >
            <Content isModal>
                <div className={styles.content}>
                    <div className={styles.imageWrapper}>
                        <div className={styles.quota}>
                            {icon}
                            <div className={styles.quotaTitle}>
                                Занято {used?.value} из {userTotalSpace?.value}
                            </div>
                            <div className={styles.quotaSeparator} />
                            <div className={styles.quotaText}>Пространство переполнено на {overquota.value}</div>
                        </div>
                    </div>
                    <div className={styles.title}>{title}</div>
                    <div className={styles.text}>{content}</div>
                    <div className={styles.footerWrapper}>
                        <Button
                            className={styles.button}
                            data-name="freeze-renewal"
                            theme="octavius"
                            sizeMode={ButtonSizeMode.small}
                            onClick={onPrimaryHandler}
                            primary={overquotaNewCommunication}
                            error={!overquotaNewCommunication}
                        >
                            {buttonTitle}
                        </Button>
                        {showClearButton && (
                            <Button
                                className={styles.button}
                                onClick={onSecondaryHandler}
                                theme="octavius"
                                sizeMode={ButtonSizeMode.small}
                                data-name="secondary"
                            >
                                {clearButtonText}
                            </Button>
                        )}
                    </div>
                </div>
            </Content>
        </Dialog>
    );
});

OverquotaModal.displayName = 'OverquotaModal';
